(() => {
  'use strict';

  const BLOCK_SELECTOR = '[data-selection-summary]';
  const normalize = (value) => String(value || '').trim().toLowerCase().replace(/&amp;/g, '&').replace(/\s+/g, ' ');
  const optionKey = (value) => normalize(value).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const parseJson = (element, fallback) => {
    try { return JSON.parse(element?.textContent || ''); } catch (error) { return fallback; }
  };

  const pluralLabel = (singular, plural, quantity) => Number(quantity) === 1 ? singular : plural;

  const getGroups = (data) => {
    if (Array.isArray(data?.groups)) return data.groups;
    if (Array.isArray(data?.products)) return data.products;
    if (Array.isArray(data)) return data;
    return [];
  };

  const getOptionMap = (variantOptions, variantId) => {
    const variant = variantOptions?.[String(variantId)];
    const map = {};
    if (!variant) return map;
    (variant.option_names || []).forEach((name, index) => {
      map[optionKey(name)] = variant[`option${index + 1}`];
    });
    return map;
  };

  const findPricingGroup = (groups, variantId, options) => {
    const direct = groups.find((group) => (group.variant_ids || group.variantIds || []).map(String).includes(String(variantId)));
    if (direct) return direct;

    // Shopify may use singular option values (Cone / Box), while the pricing
    // metafield uses plural group labels (Cones / Boxes).
    const matchLabel = (value) => normalize(value).replace(/\bcones\b/g, 'cone').replace(/\bboxes\b/g, 'box').replace(/\bwraps\b/g, 'wrap').replace(/\bpouches\b/g, 'pouch').replace(/\bcases\b/g, 'case');
    const selectedType = matchLabel(options.type);
    const selectedPack = matchLabel(options['pack-type'] || options.pack);
    return groups.find((group) => {
      const groupType = matchLabel(group.product_type);
      const groupPack = matchLabel(group.pack_type);
      return (!groupType || groupType === selectedType) && (!groupPack || groupPack === selectedPack);
    }) || null;
  };

  const getForm = (block) => {
    const product = block.closest('xo-product');
    return product?.querySelector('form[action*="/cart/add"], form[data-type="add-to-cart-form"]') || null;
  };

  const getQuantity = (form) => {
    const flavorTotal = Number.parseInt(form?.dataset?.camoMultiFlavorActualQuantity || '', 10);
    if (Number.isFinite(flavorTotal) && flavorTotal > 0) return flavorTotal;

    const nativeQuantity = Number.parseInt(form?.querySelector('input[name="quantity"]')?.value || '1', 10);
    return Number.isFinite(nativeQuantity) && nativeQuantity > 0 ? nativeQuantity : 1;
  };

  const renderItems = (container, items) => {
    container.style.setProperty('--selection-summary-columns', String(items.length));
    container.replaceChildren(...items.map(({ value, label }) => {
      const item = document.createElement('div');
      item.className = 'product-selection-summary__item';

      const valueElement = document.createElement('span');
      valueElement.className = 'product-selection-summary__value';
      valueElement.textContent = String(value);

      const labelElement = document.createElement('span');
      labelElement.className = 'product-selection-summary__label';
      labelElement.textContent = label;

      item.append(valueElement, labelElement);
      return item;
    }));
  };

  const initBlock = (block) => {
    if (block.dataset.selectionSummaryReady === 'true') return;
    block.dataset.selectionSummaryReady = 'true';

    const product = block.closest('xo-product');
    const form = getForm(block);
    const container = block.querySelector('[data-selection-summary-items]');
    const variantOptions = parseJson(block.querySelector('[data-selection-summary-options]'), {});
    const pricing = parseJson(block.querySelector('[data-selection-summary-pricing]'), null);
    const groups = getGroups(pricing);

    if (!product || !form || !container) return;

    let frame = 0;
    let lastState = '';

    const update = () => {
      frame = 0;
      const variantId = String(form.querySelector('input[name="id"], select[name="id"]')?.value || '');
      const options = getOptionMap(variantOptions, variantId);
      const type = String(options.type || '').trim();
      const packType = String(options['pack-type'] || options.pack || '').trim();
      const quantity = getQuantity(form);
      const group = findPricingGroup(groups, variantId, options);

      if (!type || !packType || !quantity) {
        block.hidden = true;
        return;
      }

      const normalizedType = normalize(type);
      const normalizedPack = normalize(packType);
      const isCones = normalizedType.includes('cone');
      const isBoxes = normalizedPack.includes('box');
      const isMasterCase = normalizedPack.includes('master case');
      const pouchCountPerUnit = Math.max(1, Number(group?.pouch_count_per_unit) || (isBoxes ? (isCones ? 12 : 25) : 1));
      const piecesPerPouch = Math.max(1, Number(
        group?.pieces_per_pouch ??
        group?.wraps_per_pouch ??
        group?.cones_per_pouch ??
        group?.units_per_pouch
      ) || (isCones ? 4 : 5));
      const piecesPerUnit = Number(group?.pieces_per_unit);
      const hasPiecesPerUnit = Number.isFinite(piecesPerUnit) && piecesPerUnit > 0;
      const totalPouches = quantity * pouchCountPerUnit;
      const totalPieces = hasPiecesPerUnit ? quantity * piecesPerUnit : totalPouches * piecesPerPouch;
      const pieceLabel = isCones ? pluralLabel('Cone', 'Cones', totalPieces) : pluralLabel('Wrap', 'Wraps', totalPieces);

      const state = [variantId, quantity, type, packType, pouchCountPerUnit, piecesPerPouch, hasPiecesPerUnit ? piecesPerUnit : ''].join(':');
      if (state === lastState && !block.hidden) return;
      lastState = state;

      const items = [{ value: totalPieces, label: pieceLabel }];
      if (isBoxes || (isMasterCase && hasPiecesPerUnit)) items.push({ value: quantity, label: isMasterCase ? pluralLabel('Master Case', 'Master Cases', quantity) : pluralLabel('Box', 'Boxes', quantity) });
      if (!hasPiecesPerUnit) items.push({ value: totalPouches, label: pluralLabel('Pouch', 'Pouches', totalPouches) });

      renderItems(container, items);
      block.hidden = false;
    };

    const scheduleUpdate = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    const scheduleSettledUpdate = () => {
      scheduleUpdate();
      [50, 150, 300].forEach((delay) => window.setTimeout(scheduleUpdate, delay));
    };

    form.addEventListener('input', scheduleUpdate);
    form.addEventListener('change', scheduleSettledUpdate);
    form.addEventListener('click', scheduleSettledUpdate);
    form.addEventListener('camo:multi-flavor-quantity-change', scheduleSettledUpdate);
    product.addEventListener('change', scheduleSettledUpdate);
    product.addEventListener('click', scheduleSettledUpdate);

    const idInput = form.querySelector('input[name="id"], select[name="id"]');
    if (idInput) new MutationObserver(scheduleSettledUpdate).observe(idInput, { attributes: true, attributeFilter: ['value'] });

    scheduleSettledUpdate();
  };

  const init = (root = document) => root.querySelectorAll?.(BLOCK_SELECTOR).forEach(initBlock);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => init(event.target.closest('section') || document));

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (node.matches(BLOCK_SELECTOR)) initBlock(node);
      init(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
